import React, {
  forwardRef,
  Ref,
  useId,
  useState,
  useImperativeHandle,
  useCallback,
} from 'react';

// Types
import {
  InputCategoryCodeProps,
  ImperativeRefInputCategoryCode,
} from './types';

// Components Styled
import { Container, InputValue, Label } from './styles';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';
import isEqual from 'lodash.isequal';

/**
 * Primary UI component for category code filtering (4 digits)
 */
function InputCategoryCode(
  {
    label = 'Category Code',
    disabled,
    onChangeInputCategoryCode,
    categoryCode,
    ...props
  }: InputCategoryCodeProps,
  innerRef: Ref<ImperativeRefInputCategoryCode>
): JSX.Element {
  const [inputValue, setInputValue] = useState<string>(categoryCode || '');

  const onChangeInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Solo permitir números y letras, máximo 4 caracteres
      if (value === '' || /^[a-zA-Z0-9]{0,4}$/.test(value)) {
        setInputValue(value);
        onChangeInputCategoryCode(value === '' ? undefined : value);
      }
    },
    [onChangeInputCategoryCode]
  );

  useImperativeHandle(innerRef, () => ({
    clearSelected: () => {
      setInputValue('');
      onChangeInputCategoryCode(undefined);
    },
  }));

  const id = useId();
  const isLandscapeTablet = useIsLandscapeTablet();

  return (
    <Container {...props}>
      {label && (
        <Label
          component={'label'}
          htmlFor={`${id}-category-code-input`}
          colorText="black"
          size={isLandscapeTablet ? 'XS' : 'S'}
        >
          {label}
        </Label>
      )}
      <InputValue
        id={`${id}-category-code-input`}
        type="text"
        disabled={disabled}
        maxLength={4}
        placeholder={'0000'}
        value={inputValue}
        onChange={onChangeInput}
        onKeyDown={(e) => {
          // Prevenir caracteres especiales
          /[+*./,\\-]/.test(e.key) && e.preventDefault();
        }}
      />
    </Container>
  );
}

export default React.memo(forwardRef(InputCategoryCode), isEqual);
